import { useEffect, useRef } from 'react'
import { Editor, useValue } from '@tldraw/tldraw'

/**
 * Hook to track board interaction state (dragging, pointing, panning).
 * Uses tldraw's reactive system for reliable state detection.
 *
 * @param editor - The tldraw editor instance
 * @returns A ref containing the current interaction state
 *
 * @example
 * const isInteractingRef = useBoardInteraction(editor)
 * // Use in callbacks:
 * if (isInteractingRef.current) {
 *   // User is dragging/interacting
 * }
 */
export function useBoardInteraction(editor: Editor | null) {
  // Track drag/interaction state via ref for use in store listener callbacks
  // The ref is synced from useValue which is reliable (unlike checking editor.inputs directly in callbacks)
  const isInteractingRef = useRef(false)

  // Use tldraw's reactive system to track interaction state (drag/resize/pan)
  // This updates SYNCHRONOUSLY with editor.inputs, unlike reading editor.inputs
  // directly inside store listener callbacks (which is stale/unreliable)
  const isInteractingValue = useValue(
    'board-is-interacting',
    () => editor?.inputs.isDragging || editor?.inputs.isPointing || editor?.inputs.isPanning || false,
    [editor]
  )

  // Sync the reactive value to a ref for use in store listener callbacks
  // This runs synchronously when isInteractingValue changes
  useEffect(() => {
    isInteractingRef.current = isInteractingValue
  }, [isInteractingValue])

  return isInteractingRef
}
